import React, { useEffect, useState } from "react";
import { BASE_URL } from "../utils/config";
import { getAuthHeaders, getAuthToken } from "../utils/auth";
import { useNavigate } from "react-router-dom";
import "../styles/AdminDashboard.css";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    tours: 0,
    bookings: 0,
    users: 0,
    contacts: 0,
    blogs: 0,
    revenue: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const authHeaders = getAuthHeaders(false);

      // Get tour count
      const toursRes = await fetch(`${BASE_URL}/tours/count`);
      const toursData = await toursRes.json();

      // Get bookings
      let bookingsCount = 0;
      let recentBookingsData = [];
      try {
        const bookingsRes = await fetch(`${BASE_URL}/booking`, {
          headers: authHeaders,
        });
        if (bookingsRes.ok) {
          const bookingsData = await bookingsRes.json();
          if (bookingsData.success) {
            bookingsCount = bookingsData.data?.length || 0;
            recentBookingsData = bookingsData.data?.slice(0, 5) || [];
          }
        }
      } catch (err) {
        // Fail silent
      }

      // Get users count
      let usersCount = 0;
      try {
        const usersRes = await fetch(`${BASE_URL}/users`, {
          headers: authHeaders,
        });
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          if (usersData.success) {
            usersCount = usersData.data?.length || 0;
          }
        }
      } catch (err) {
        // Fail silent
      }

      // Get contacts count
      let contactsCount = 0;
      let recentContactsData = [];
      try {
        const contactsRes = await fetch(`${BASE_URL}/contact`, {
          headers: authHeaders,
        });
        if (contactsRes.ok) {
          const contactsData = await contactsRes.json();
          if (contactsData.success) {
            contactsCount = contactsData.data?.length || 0;
            recentContactsData = contactsData.data?.slice(0, 5) || [];
          }
        }
      } catch (err) {
        // Fail silent
      }

      // Get blogs count
      let blogsCount = 0;
      try {
        const blogsRes = await fetch(`${BASE_URL}/blogs`);
        if (blogsRes.ok) {
          const blogsData = await blogsRes.json();
          blogsCount = Array.isArray(blogsData) ? blogsData.length : 0;
        }
      } catch (err) {
        // Fail silent
      }

      // Calculate revenue from bookings (assuming average tour price)
      const revenue = bookingsCount * 100; // Placeholder calculation

      setStats({
        tours: toursData.data || 0,
        bookings: bookingsCount,
        users: usersCount,
        contacts: contactsCount,
        blogs: blogsCount,
        revenue,
      });

      setRecentBookings(recentBookingsData);
      setRecentContacts(recentContactsData);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const statCards = [
    {
      title: "Total Tours",
      value: stats.tours,
      icon: "ri-map-pin-line",
      color: "#faa935",
      bgColor: "rgba(250, 169, 53, 0.1)",
      link: "/admin/tours",
    },
    {
      title: "Total Bookings",
      value: stats.bookings,
      icon: "ri-bookmark-line",
      color: "#ff7e01",
      bgColor: "rgba(255, 126, 1, 0.1)",
      link: "/admin/bookings",
    },
    {
      title: "Total Users",
      value: stats.users,
      icon: "ri-user-line",
      color: "#0b2727",
      bgColor: "rgba(11, 39, 39, 0.1)",
      link: "/admin/users",
    },
    {
      title: "Contact Messages",
      value: stats.contacts,
      icon: "ri-mail-line",
      color: "#6e7074",
      bgColor: "rgba(110, 112, 116, 0.1)",
      link: "/admin/contacts",
    },
    {
      title: "Blogs",
      value: stats.blogs,
      icon: "ri-article-line",
      color: "#faa935",
      bgColor: "rgba(250, 169, 53, 0.1)",
      link: "/admin/blogs",
    },
    {
      title: "Revenue",
      value: `$${stats.revenue.toLocaleString()}`,
      icon: "ri-money-dollar-circle-line",
      color: "#ff7e01",
      bgColor: "rgba(255, 126, 1, 0.1)",
    },
  ];

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loader"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-welcome">
        <h3>Welcome back, Admin!</h3>
        <p>Here's what's happening with your travel agency today.</p>
      </div>

      <div className="dashboard-stats">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="stat-card"
            onClick={() => stat.link && navigate(stat.link)}
            style={{ cursor: stat.link ? "pointer" : "default" }}
          >
            <div
              className="stat-icon"
              style={{ color: stat.color, backgroundColor: stat.bgColor }}
            >
              <i className={stat.icon}></i>
            </div>
            <div className="stat-content">
              <h4>{stat.value}</h4>
              <p>{stat.title}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-content-grid">
        <div className="dashboard-section">
          <div className="section-header">
            <h4>Recent Bookings</h4>
            <button className="view-all-btn" onClick={() => navigate("/admin/bookings")}>
              View All
            </button>
          </div>
          {recentBookings.length === 0 ? (
            <div className="empty-mini">
              <i className="ri-bookmark-line"></i>
              <p>No recent bookings</p>
            </div>
          ) : (
            <div className="recent-list">
              {recentBookings.map((booking) => (
                <div key={booking._id} className="recent-item">
                  <div className="recent-item-info">
                    <h5>{booking.tourName}</h5>
                    <p>{booking.fullName} • {booking.groupSize} {booking.groupSize === 1 ? 'person' : 'people'}</p>
                  </div>
                  <span className="recent-date">{formatDate(booking.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dashboard-section">
          <div className="section-header">
            <h4>Recent Contacts</h4>
            <button className="view-all-btn" onClick={() => navigate("/admin/contacts")}>
              View All
            </button>
          </div>
          {recentContacts.length === 0 ? (
            <div className="empty-mini">
              <i className="ri-mail-line"></i>
              <p>No recent messages</p>
            </div>
          ) : (
            <div className="recent-list">
              {recentContacts.map((contact) => (
                <div key={contact._id} className="recent-item">
                  <div className="recent-item-info">
                    <h5>{contact.name}</h5>
                    <p>{contact.email}</p>
                  </div>
                  <span className="recent-date">{formatDate(contact.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-quick-actions">
        <h4>Quick Actions</h4>
        <div className="quick-actions-grid">
          <div className="quick-action-card" onClick={() => navigate("/admin/tours/add")}>
            <i className="ri-add-circle-line"></i>
            <h5>Add New Tour</h5>
            <p>Create a new tour package</p>
          </div>
          <div className="quick-action-card" onClick={() => navigate("/admin/blogs/add")}>
            <i className="ri-article-line"></i>
            <h5>Add New Blog</h5>
            <p>Create a new blog post</p>
          </div>
          <div className="quick-action-card" onClick={() => navigate("/admin/tours")}>
            <i className="ri-file-list-line"></i>
            <h5>Manage Tours</h5>
            <p>View and edit all tours</p>
          </div>
          <div className="quick-action-card" onClick={() => window.open("/", "_blank")}>
            <i className="ri-eye-line"></i>
            <h5>View Website</h5>
            <p>See how users see your site</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

